import { useEffect } from 'react';
import { useLoaded } from 'src/store/load-context';
const pageTitle = (title) => `${title} | Personal Property`;
import LoadingBackdrop from 'src/components/LoadingBackdrop';
import WarningModal from 'src/components/modal/WarningModal';
import SkipToLastStepModal from 'src/components/modal/SkipToLastStepModal';
import { useContext } from 'react';
import { AppContext } from 'src/store/app-context';
import StepperContext from 'src/store/stepper-context';
import AuthContext from 'src/store/auth-context';

import { useModal } from 'src/services/modal';
import useAutoLogout from 'src/hooks/useAutoLogout';
import useRefreshSessionModal from 'src/hooks/useRefreshSession';
import { colors } from 'src/styles/theme';

/**
 * The Page component serves multiple purposes:
 * 1. It sets the document title based on the provided title prop.
 * 2. It integrates session management features including auto-logout and session refresh prompts.
 * 3. It renders a loading backdrop and a warning modal based on the session state.
 *
 * @param {Object} props - The component props.
 * @param {string} props.title - The title to set for the page.
 * @param {string} props.refreshSessionURL - URL used for refreshing the session.
 * @param {Object} props.refreshSessionContext - Context for managing session state.
 * @param {Function} props.refreshSessionAPI - API function to call for refreshing the session.
 * @param {React.ReactNode} props.children - The content to render inside the component.
 */
const Page = ({ title, sessionContext, refreshSessionURL, refreshSessionAPI, children }) => {
  const { loaded } = useLoaded();
  const { setStepNo, stepNo } = useContext(StepperContext);
  const { shouldSkipToLastStep } = useContext(AppContext);
  const { isLoggedIn } = useContext(AuthContext);
  const {
    closeModal: closeSkipToLastStepModal,
    open: skipToLastStepModalIsOpen,
    openModal: openSkipToLastStepModalIsOpen,
  } = useModal();

  //init pages auto logout
  useAutoLogout({ context: sessionContext });

  const { handleRefresh, open, closeModal, secondsLeft } = useRefreshSessionModal({
    api: refreshSessionAPI,
    context: sessionContext,
    url: refreshSessionURL,
  });

  useEffect(() => {
    if (stepNo > 1 && stepNo < 4 && shouldSkipToLastStep && isLoggedIn) {
      openSkipToLastStepModalIsOpen();
    } else {
      closeSkipToLastStepModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, shouldSkipToLastStep, stepNo]);

  useEffect(() => {
    document.title = pageTitle(title || '');
  }, [title]);

  const style = {
    background: colors.grey[50],
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    minHeight: '100vh',
  };

  return (
    <>
      <WarningModal
        secondsLeft={secondsLeft}
        open={open}
        closeModal={closeModal}
        onRefreshClick={handleRefresh}
      />

      <SkipToLastStepModal
        closeModal={closeSkipToLastStepModal}
        open={skipToLastStepModalIsOpen}
        onContinue={() => {
          setStepNo(4);
        }}
      />
      <LoadingBackdrop loading={!loaded} />
      <div style={style}>{children}</div>
    </>
  );
};

export default Page;
